
import { getImageSize } from '../libraries/utils';

export default {
  name: 'SImageLoader',
  props: {
    uuid: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      required: true,
    },
    alt: {
      type: String,
      default: 'Image non chargée',
    },
    assets: {
      type: String,
      default: null,
    },
    noPlaceholder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      src: '',
      srcsetWebP: '',
      srcsetJpg: '',
      imageHeight: null,
      imageWidth: null,
      containerSize: null,
      resizeObserver: null,
      imageLoaded: false,
      sizes: {
        xs: 160,
        sm: 320,
        md: 640,
        lg: 1280,
      },
      orientation: 'landscape',
      isPlaceholder: false,
    };
  },
  watch: {
    uuid() {
      this.src = '';
      this.srcsetWebP = '';
      this.srcsetJpg = '';
      this.imageLoaded = false;
      this.isPlaceholder = false;
      this.initLoader();
    },
  },
  mounted() {
    this.initLoader();
  },
  beforeDestroy() {
    if (this.resizeObserver && this.imageLoaded) {
      this.resizeObserver.unobserve(this.$refs.imageLoader);
    }
  },
  methods: {
    initLoader() {
      // Update the different dimensions according to the 'avatar' type
      if (this.type === 'avatar') {
        this.sizes = {
          xs: 32,
          sm: 64,
          md: 120,
          lg: 256,
        };
      }

      const jpgExtension = `jpg${this.noPlaceholder ? '?no-placeholder' : ''}`;
      const webpExtension = `webp${this.noPlaceholder ? '?no-placeholder' : ''}`;

      this.$nextTick(() => {
        if (!this.uuid) {
          this.src = '/img/placeholders/photo_placeholder2.svg';
          this.srcsetWebP = '';
          this.srcsetJpg = '';
          this.isPlaceholder = true;
        } else if (this.type === 'upload') {
          this.src = `${process.env.VUE_APP_ASSETS_URL}${this.assets}${this.uuid}.${jpgExtension}`;
          this.srcsetWebP = `${process.env.VUE_APP_ASSETS_URL}${this.assets}${this.uuid}.${webpExtension}`;
          this.srcsetJpg = `${process.env.VUE_APP_ASSETS_URL}${this.assets}${this.uuid}.${jpgExtension}`;
        } else if (this.type === 'map') {
          this.src = `${process.env.VUE_APP_ASSETS_URL}${this.type}/${this.uuid}.${jpgExtension}`;
          this.srcsetWebP = `${process.env.VUE_APP_ASSETS_URL}${this.type}/${this.uuid}.${webpExtension}`;
          this.srcsetJpg = `${process.env.VUE_APP_ASSETS_URL}${this.type}/${this.uuid}.${jpgExtension}`;
        } else {
          // Set container dimensions only for images with different possible sizes
          this.setContainerSize();
          // Default picture
          const size = getImageSize(this.$refs.imageLoader?.parentElement.clientWidth, this.sizes);
          this.src = `${process.env.VUE_APP_ASSETS_URL}${this.type}/${size}/${this.uuid}.${jpgExtension}`;

          // WebP srcset
          this.srcsetWebP = `${process.env.VUE_APP_ASSETS_URL}${this.type}/lg/${this.uuid}.${webpExtension} ${this.sizes.lg}w,
            ${process.env.VUE_APP_ASSETS_URL}${this.type}/md/${this.uuid}.${webpExtension} ${this.sizes.md}w,
            ${process.env.VUE_APP_ASSETS_URL}${this.type}/sm/${this.uuid}.${webpExtension} ${this.sizes.sm}w,
            ${process.env.VUE_APP_ASSETS_URL}${this.type}/xs/${this.uuid}.${webpExtension} ${this.sizes.xs}w`;

          // Jpg srcset
          this.srcsetJpg = `${process.env.VUE_APP_ASSETS_URL}${this.type}/lg/${this.uuid}.${jpgExtension} ${this.sizes.lg}w,
            ${process.env.VUE_APP_ASSETS_URL}${this.type}/md/${this.uuid}.${jpgExtension} ${this.sizes.md}w,
            ${process.env.VUE_APP_ASSETS_URL}${this.type}/sm/${this.uuid}.${jpgExtension} ${this.sizes.sm}w,
            ${process.env.VUE_APP_ASSETS_URL}${this.type}/xs/${this.uuid}.${jpgExtension} ${this.sizes.xs}w`;
        }
      });
    },
    setContainerSize() {
      // Change the size of the container only if it is larger
      // according to the different image sizes
      if (this.$refs.imageLoader && (!this.containerSize || this.$refs.imageLoader.parentElement.clientWidth > this.containerSize)) {
        const imageWidth = this.$refs.imageLoader.parentElement.clientWidth;
        if (imageWidth > this.sizes.md) {
          this.containerSize = this.sizes.lg;
        } else if (imageWidth > this.sizes.sm) {
          this.containerSize = this.sizes.md;
        } else if (imageWidth > this.sizes.xs) {
          this.containerSize = this.sizes.sm;
        } else {
          this.containerSize = imageWidth;
        }
      }
    },
    onLoad() {
      this.imageLoaded = true;
      this.$emit('load');
      const image = this.$refs.image;
      if (image) {
      // On teste l'orientation
        if (image.naturalHeight > image.naturalWidth) {
          this.orientation = 'portrait';
        } else if (image.naturalHeight === image.naturalWidth) {
          this.orientation = 'carre';
        }
        this.imageHeight = image.naturalHeight;
        this.imageWidth = image.naturalWidth;
      }
      this.$emit('orientation', this.orientation);

      // Observe the resizing of the container only if it is already defined
      if (this.$refs.imageLoader && this.containerSize !== null) {
        // Observe container resizing
        this.resizeObserver = new ResizeObserver(() => this.setContainerSize());
        this.resizeObserver.observe(this.$refs.imageLoader);
      }
    },
    onError() {
      this.$emit('error');
      if (!this.noPlaceholder) {
        this.src = '/img/placeholders/photo_placeholder2.svg';
        this.srcsetWebP = '';
        this.srcsetJpg = '';
        this.isPlaceholder = true;
      }
    },
  },
};
